<div class="operator-edit-container" [ngClass]="{ 'p-4': isHandset(), 'p-6': !isHandset() }">
    <!-- Header -->
    <div class="mb-6">
        <div class="flex items-center mb-2">
            <button mat-icon-button type="button" (click)="onCancel()" class="mr-3 text-gray-600 hover:text-gray-800">
                <mat-icon>arrow_back</mat-icon>
            </button>
            <div>
                <h1 class="text-xl sm:text-2xl font-semibold text-gray-900">
                    Edit Operator
                </h1>
                @if (operator) {
                <p class="text-sm text-gray-600">{{ getOperatorDisplayName() }}</p>
                }
            </div>
        </div>
    </div>

    <!-- Loading State -->
    @if (isLoading) {
    <div class="bg-white rounded-lg shadow-sm border p-8 text-center">
        <div class="flex flex-col items-center space-y-4">
            <mat-spinner diameter="50"></mat-spinner>
            <div>
                <h3 class="text-lg font-medium text-gray-900 mb-2">Loading operator details...</h3>
                <p class="text-gray-500">Please wait while we fetch the operator information.</p>
            </div>
        </div>
    </div>
    }

    <!-- Edit Form -->
    @else if (operator) {
    <div class="bg-white rounded-lg shadow-sm border p-6 max-w-2xl">
        <!-- Success Message -->
        @if (successMessage) {
        <div class="bg-green-50 border border-green-200 rounded-md p-4 mb-6">
            <div class="flex">
                <mat-icon class="text-green-400 mr-2">check_circle</mat-icon>
                <div class="text-sm text-green-800">{{ successMessage }}</div>
                <button type="button" class="ml-auto text-green-400 hover:text-green-600" (click)="onClearSuccess()">
                    <mat-icon class="text-sm">close</mat-icon>
                </button>
            </div>
        </div>
        }

        <!-- Error Message -->
        @if (errorMessage) {
        <div class="bg-red-50 border border-red-200 rounded-md p-4 mb-6">
            <div class="flex">
                <mat-icon class="text-red-400 mr-2">error</mat-icon>
                <div class="text-sm text-red-800">{{ errorMessage }}</div>
                <button type="button" class="ml-auto text-red-400 hover:text-red-600" (click)="onClearError()">
                    <mat-icon class="text-sm">close</mat-icon>
                </button>
            </div>
        </div>
        }

        <form [formGroup]="editOperatorForm" (ngSubmit)="onSubmit()" class="space-y-6">
            <!-- Operator Info Section -->
            <div class="border-b border-gray-200 pb-6">
                <h3 class="text-lg font-medium text-gray-900 mb-4">Operator Information</h3>

                <!-- Current Operator Details -->
                <div class="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                    <div class="flex items-center space-x-3">
                        <div class="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center">
                            <span class="text-white font-semibold">
                                {{ (operator.firstName?.charAt(0) || '') + (operator.lastName?.charAt(0) || '') }}
                            </span>
                        </div>
                        <div>
                            <p class="font-medium text-blue-900">Current Operator</p>
                            <p class="text-sm text-blue-700">ID: {{ operator.id }}</p>
                            <!-- @if (operator.externalId) {
                            <p class="text-xs text-blue-600">External ID: {{ operator.externalId }}</p>
                            } -->
                        </div>
                    </div>
                </div>

                <div class="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    <!-- First Name -->
                    <div>
                        <mat-form-field appearance="fill" class="w-full">
                            <mat-label>First Name *</mat-label>
                            <input matInput formControlName="firstName" placeholder="Enter first name">
                            @if(isFieldInvalid('firstName'))
                            {
                            <mat-error>
                                {{ getFieldError('firstName') }}
                            </mat-error>
                            }

                        </mat-form-field>
                    </div>

                    <!-- Last Name -->
                    <div>
                        <mat-form-field appearance="fill" class="w-full">
                            <mat-label>Last Name *</mat-label>
                            <input matInput formControlName="lastName" placeholder="Enter last name">
                            @if(isFieldInvalid('lastName'))
                            {
                            <mat-error>
                                {{ getFieldError('lastName') }}
                            </mat-error>
                            }
                        </mat-form-field>
                    </div>
                </div>

                <!-- Email -->
                <div class="mt-6">
                    <mat-form-field appearance="fill" class="w-full">
                        <mat-label>Email Address *</mat-label>
                        <input matInput type="email" formControlName="email" placeholder="Enter email address">
                        @if(isFieldInvalid('email'))
                        {
                        <mat-error>
                            {{ getFieldError('email') }}
                        </mat-error>
                        }
                    </mat-form-field>
                </div>

                <!-- Status Toggle -->
                <div class="mt-6">
                    <label class="flex items-center space-x-3">
                        <span class="text-sm font-medium text-gray-700">Status:</span>
                        <mat-slide-toggle formControlName="enabled" color="primary">
                            <span class="text-sm" [class.text-green-600]="editOperatorForm.get('enabled')?.value"
                                [class.text-gray-500]="!editOperatorForm.get('enabled')?.value">
                                {{ editOperatorForm.get('enabled')?.value ? 'Active' : 'Inactive' }}
                            </span>
                        </mat-slide-toggle>
                    </label>
                    <p class="text-xs text-gray-500 mt-1">
                        Inactive operators cannot access the system
                    </p>
                </div>
            </div>

            <!-- Statistics Section -->
            @if (operator.parkingSpaces || operator.licenses) {
            <div class="border-b border-gray-200 pb-6">
                <h3 class="text-lg font-medium text-gray-900 mb-4">Current Assignments</h3>

                <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <!-- Parking Spaces -->
                    <div class="bg-blue-50 rounded-lg p-4">
                        <div class="flex items-center">
                            <mat-icon class="text-blue-600 mr-3">local_parking</mat-icon>
                            <div>
                                <p class="text-xs text-blue-600 font-medium">Parking Spaces</p>
                                <p class="text-2xl font-semibold text-blue-900">{{ operator.parkingSpaces?.length || 0
                                    }}</p>
                            </div>
                        </div>
                    </div>

                    <!-- Licenses -->
                    <div class="bg-green-50 rounded-lg p-4">
                        <div class="flex items-center">
                            <mat-icon class="text-green-600 mr-3">description</mat-icon>
                            <div>
                                <p class="text-xs text-green-600 font-medium">Licenses</p>
                                <p class="text-2xl font-semibold text-green-900">{{ operator.licenses?.length || 0 }}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            }

            <!-- Form Actions -->
            <div class="flex flex-col sm:flex-row gap-3 pt-6">
                <button mat-button type="button" (click)="onCancel()" [disabled]="isSubmitting"
                    class="w-full sm:w-auto">
                    Cancel
                </button>

                <button matButton="filled" color="primary" type="submit"
                    [disabled]="editOperatorForm.invalid || isSubmitting" class="w-full sm:w-auto">
                    {{ isSubmitting ? 'Updating...' : 'Update Operator' }}
                </button>
            </div>
        </form>
    </div>
    }

    <!-- Error State -->
    @else if (errorMessage) {
    <div class="bg-white rounded-lg shadow-sm border p-12 text-center">
        <div class="flex flex-col items-center space-y-4">
            <mat-icon class="text-red-400 text-6xl">error_outline</mat-icon>
            <div class="space-y-2">
                <h3 class="text-lg font-medium text-gray-900">Unable to Load Operator</h3>
                <p class="text-gray-500 max-w-sm">{{ errorMessage }}</p>
            </div>
            <button mat-raised-button color="primary" (click)="onCancel()">
                Back to Operators
            </button>
        </div>
    </div>
    }
</div>